import { limit, orderBy, where } from 'firebase/firestore';

import { ContactInquirySchema } from '@kilat/shared';

import { createRepo } from './_base.js';

/**
 * Warm leads routed to this agent (gap G25).
 *
 * Reads `contactInquiries` — the collection the marketing contact form has
 * always written to and which, until leads were routed, nothing anywhere read.
 *
 * The query is scoped to `assignedAgentId == me` AND an open `leadStatus`.
 * Both halves matter: the first is what `firestore.rules` allows an agent to
 * see (an inquiry carries a stranger's name, email and message, so an agent
 * may read only the ones actually assigned to them), and the second keeps a
 * closed lead out of the queue.
 */
const repo = createRepo({
  path: 'contactInquiries',
  schema: ContactInquirySchema,
  name: 'contactInquiries',
});

/**
 * Small on purpose. A queue longer than this is not a queue, it is a backlog —
 * and the router caps open leads per agent well below it anyway.
 */
const MAX_VISIBLE_LEADS = 10;

export const leadsRepo = {
  ...repo,
  /** Open leads assigned to one agent, oldest first — the one waiting longest is the most urgent. */
  listForAgent(agentId: string) {
    return repo.listWith(
      where('assignedAgentId', '==', agentId),
      where('leadStatus', 'in', ['assigned', 'contacted']),
      orderBy('createdAt', 'asc'),
      limit(MAX_VISIBLE_LEADS),
    );
  },
};
